import * as styles from "./TimeRangePickerAxis.css";

import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";

import { useTimeRangePickerContext } from "./TimeRangePicker.context";
import type { TimeRangePickerAxisProps } from "./TimeRangePicker.types";
import { fromMinutes, ticksBetween } from "./timeMath";

const LABEL_SPACING = 1.5 * 1.25;

export function TimeRangePickerAxis({
  className,
  format = fromMinutes,
}: TimeRangePickerAxisProps): JSX.Element {
  const { view, majorStep, percentOf } = useTimeRangePickerContext();

  const ticks = ticksBetween(view.begin, view.end, majorStep);

  const axisRef = useRef<HTMLDivElement>(null);

  const [stride, setStride] = useState(1);

  useEffect(() => {
    const axis = axisRef.current;

    if (!axis) {
      return;
    }

    const measure = () => {
      const label = axis.firstElementChild;
      const labelWidth = label instanceof HTMLElement ? label.offsetWidth : 0;
      const slot = axis.clientWidth / Math.max(1, ticks.length - 1);

      setStride(
        0 < labelWidth && 0 < slot
          ? Math.max(1, Math.ceil((labelWidth * LABEL_SPACING) / slot))
          : 1,
      );
    };

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(axis);

    return () => observer.disconnect();
  }, [ticks.length]);

  return (
    <div
      ref={axisRef}
      data-slot="time-range-picker-axis"
      className={clsx(styles.axis, className)}
      aria-hidden
    >
      {ticks.map((tick, index) =>
        0 === index % stride ? (
          <span
            key={tick}
            className={styles.tick}
            data-edge={
              tick === view.begin
                ? "begin"
                : tick === view.end
                  ? "end"
                  : undefined
            }
            style={{ left: `${percentOf(tick)}%` }}
          >
            {format(tick)}
          </span>
        ) : null,
      )}
    </div>
  );
}
